'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  Alert,
  Box,
  CircularProgress,
  Typography,
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { VtkDatasetInfo } from '../types/vtk';
import { loadVtkModules } from '../lib/loadVtkModules';
import { createVtkScene, type VtkScene } from '../lib/createVtkScene';
import {
  applyPaletteToMapper,
  resolveScalarRange,
  type ScalarRange,
} from '../lib/vtkScalarStyling';

type SectionViewerProps = {
  reValue: number;
  sharedScalarRange: ScalarRange | null;
};

type SectionViewDirection = 'x+' | 'x-' | 'z+' | 'z-';

type SectionResponse =
  | {
      message: string;
      scalarField?: string;
      x_inlet?: VtkDatasetInfo | null;
      z_inlet?: VtkDatasetInfo | null;
      outlet?: VtkDatasetInfo | null;
    }
  | {
      message: string;
      scalarField?: string;
      mesh?: VtkDatasetInfo | null;
    };

type SectionCardProps = {
  title: string;
  description?: string;
  dataset: VtkDatasetInfo | null;
  activeScalar?: string | null;
  loading?: boolean;
  viewDirection: SectionViewDirection;
  scalarRange: ScalarRange | null;
};

function getActiveScalarEntry(
  dataset: VtkDatasetInfo,
  activeScalar: string | null,
) {
  if (
    activeScalar &&
    dataset.pointData[activeScalar] &&
    dataset.pointData[activeScalar].numberOfComponents === 1
  ) {
    return dataset.pointData[activeScalar];
  }

  return Object.values(dataset.pointData).find(
    (arr) => arr.numberOfComponents === 1,
  );
}

function getProjectedSize(
  bounds: [number, number, number, number, number, number],
  view: SectionViewDirection,
) {
  const [xmin, xmax, ymin, ymax, zmin, zmax] = bounds;

  switch (view) {
    case 'x+':
    case 'x-':
      return {
        width: Math.max(ymax - ymin, 1e-6),
        height: Math.max(zmax - zmin, 1e-6),
      };

    case 'z+':
    case 'z-':
      return {
        width: Math.max(xmax - xmin, 1e-6),
        height: Math.max(ymax - ymin, 1e-6),
      };
  }
}

function getViewportAspect(scene: VtkScene) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const size = (scene.renderWindow as any).getViews?.()?.[0]?.getSize?.() ?? [
    1, 1,
  ];
  const width = Math.max(size[0] ?? 1, 1);
  const height = Math.max(size[1] ?? 1, 1);
  return width / height;
}

function setSectionCamera(
  scene: VtkScene,
  bounds: [number, number, number, number, number, number] | undefined,
  view: SectionViewDirection,
) {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  if (!bounds || bounds.length !== 6) {
    (scene.renderer as any).resetCamera();
    (scene.renderer as any).resetCameraClippingRange();
    (scene.renderWindow as any).render();
    return;
  }

  const [xmin, xmax, ymin, ymax, zmin, zmax] = bounds;

  const cx = 0.5 * (xmin + xmax);
  const cy = 0.5 * (ymin + ymax);
  const cz = 0.5 * (zmin + zmax);

  const dx = Math.max(xmax - xmin, 1e-6);
  const dy = Math.max(ymax - ymin, 1e-6);
  const dz = Math.max(zmax - zmin, 1e-6);
  const dist = 2.5 * Math.max(dx, dy, dz);

  const { width, height } = getProjectedSize(bounds, view);
  const aspect = getViewportAspect(scene);

  const camera = (scene.renderer as any).getActiveCamera();
  camera.setParallelProjection(true);
  camera.setFocalPoint(cx, cy, cz);

  switch (view) {
    case 'x+':
      camera.setPosition(cx + dist, cy, cz);
      camera.setViewUp(0, 0, 1);
      break;
    case 'x-':
      camera.setPosition(cx - dist, cy, cz);
      camera.setViewUp(0, 0, 1);
      break;
    case 'z+':
      camera.setPosition(cx, cy, cz + dist);
      camera.setViewUp(0, 1, 0);
      break;
    case 'z-':
      camera.setPosition(cx, cy, cz - dist);
      camera.setViewUp(0, 1, 0);
      break;
  }

  const padding = 1.08;
  const parallelScale = 0.5 * Math.max(height, width / aspect) * padding;
  camera.setParallelScale(parallelScale);

  (scene.renderer as any).resetCameraClippingRange();
  (scene.renderWindow as any).render();
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

async function applyDatasetToScene(
  scene: VtkScene,
  dataset: VtkDatasetInfo,
  activeScalar: string | null,
  viewDirection: SectionViewDirection,
  scalarRange: ScalarRange | null,
) {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const vtk = await loadVtkModules();

  const points = (vtk.vtkPoints as any).newInstance();
  points.setData(new Float32Array(dataset.points.flat()), 3);
  (scene.polydata as any).setPoints(points);

  if (dataset.cells.connectivity.length > 0) {
    const flatCells: number[] = [];
    for (const cell of dataset.cells.connectivity) {
      flatCells.push(cell.length, ...cell);
    }

    const cellArray = (vtk.vtkCellArray as any).newInstance({
      values: Uint32Array.from(flatCells),
    });

    (scene.polydata as any).setPolys(cellArray);
  } else {
    (scene.polydata as any).setPolys(
      (vtk.vtkCellArray as any).newInstance({ values: new Uint32Array() }),
    );
  }

  const scalarEntry = getActiveScalarEntry(dataset, activeScalar);

  if (
    scalarEntry &&
    scalarEntry.numberOfComponents === 1 &&
    Array.isArray(scalarEntry.values) &&
    scalarEntry.values.length > 0
  ) {
    const scalarValues = scalarEntry.values as number[];

    (scene.polydata as any).getPointData().setScalars(
      (vtk.vtkDataArray as any).newInstance({
        name: scalarEntry.name,
        values: Float32Array.from(scalarValues),
        numberOfComponents: 1,
      }),
    );

    (scene.mapper as any).setScalarVisibility(true);
    (scene.mapper as any).setColorModeToMapScalars();
    (scene.mapper as any).setScalarModeToUsePointData();

    const resolvedRange = resolveScalarRange(scalarValues, scalarRange);

    if (resolvedRange) {
      applyPaletteToMapper(vtk, scene.mapper, resolvedRange);
    } else {
      (scene.mapper as any).setScalarVisibility(false);
    }
  } else {
    (scene.polydata as any).getPointData().setScalars(null);
    (scene.mapper as any).setScalarVisibility(false);
  }

  (scene.polydata as any).modified();
  (scene.mapper as any).modified();
  setSectionCamera(scene, dataset.metadata.bounds, viewDirection);
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

function SectionCard({
  title,
  description,
  dataset,
  activeScalar = null,
  loading = false,
  viewDirection,
  scalarRange,
}: SectionCardProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sceneRef = useRef<VtkScene | null>(null);
  const latestDatasetRef = useRef<VtkDatasetInfo | null>(null);
  const latestViewDirectionRef = useRef<SectionViewDirection>(viewDirection);

  useEffect(() => {
    latestDatasetRef.current = dataset;
  }, [dataset]);

  useEffect(() => {
    latestViewDirectionRef.current = viewDirection;
  }, [viewDirection]);

  useEffect(() => {
    let mounted = true;
    let resizeObserver: ResizeObserver | null = null;

    async function init() {
      const container = containerRef.current;
      if (!container) return;

      const vtk = await loadVtkModules();
      if (!mounted) return;

      const scene = createVtkScene(vtk, container);
      sceneRef.current = scene;

      /* eslint-disable @typescript-eslint/no-explicit-any */
      (scene.renderer as any).setBackground(0.07, 0.07, 0.07);
      (scene.renderer as any).getActiveCamera().setParallelProjection(true);
      /* eslint-enable @typescript-eslint/no-explicit-any */

      resizeObserver = new ResizeObserver(() => {
        scene.resize();

        const currentDataset = latestDatasetRef.current;
        const currentViewDirection = latestViewDirectionRef.current;

        if (currentDataset?.metadata?.bounds) {
          setSectionCamera(
            scene,
            currentDataset.metadata.bounds,
            currentViewDirection,
          );
        } else {
          /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
          (scene.renderWindow as any).render();
        }
      });

      resizeObserver.observe(container);
    }

    void init();

    return () => {
      mounted = false;
      resizeObserver?.disconnect();
      sceneRef.current?.destroy();
      sceneRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!dataset || !sceneRef.current) return;

    void applyDatasetToScene(
      sceneRef.current,
      dataset,
      activeScalar,
      viewDirection,
      scalarRange,
    );
  }, [dataset, activeScalar, viewDirection, scalarRange]);

  useEffect(() => {
    if (!dataset || !sceneRef.current) return;

    setSectionCamera(sceneRef.current, dataset.metadata.bounds, viewDirection);
  }, [dataset, viewDirection]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant='subtitle1'
          sx={{
            fontSize: '1.15rem',
            fontWeight: 400,
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>

        {description && (
          <Tooltip
            arrow
            title={description}
            slotProps={{
              tooltip: {
                sx: {
                  maxWidth: 300,
                  fontSize: '0.95rem',
                },
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'text.secondary',
                cursor: 'help',
              }}
            >
              <InfoOutlinedIcon fontSize='small' />
            </Box>
          </Tooltip>
        )}
      </Box>

      <Box
        sx={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          bgcolor: '#111',
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0,0,0,0.35)',
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && !dataset && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 2,
              textAlign: 'center',
              color: 'text.secondary',
              bgcolor: '#111',
            }}
          >
            <Typography variant='body2'>
              No data available for this section
            </Typography>
          </Box>
        )}

        <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      </Box>
    </Box>
  );
}

export default function SectionViewer({
  reValue,
  sharedScalarRange,
}: SectionViewerProps) {
  // TODO(local-auth-bypass): router used for auth redirects.
  const router = useRouter();
  const [xInlet, setXInlet] = useState<VtkDatasetInfo | null>(null);
  const [zInlet, setZInlet] = useState<VtkDatasetInfo | null>(null);
  const [outlet, setOutlet] = useState<VtkDatasetInfo | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadSections() {
      setLoading(true);
      setError(null);

      try {
        // TODO(local-auth-bypass): token validation and login redirect.
        const token = localStorage.getItem('access_token');
        if (!token) {
          router.replace('/login');
          return;
        }

        const apiUrl = process.env.NEXT_PUBLIC_API_URL;
        if (!apiUrl) {
          throw new Error('NEXT_PUBLIC_API_URL is not defined');
        }

        const [xResponse, zResponse, outletResponse] = await Promise.all([
          fetch(
            `${apiUrl}/mesh/inlet-x?re=${encodeURIComponent(String(reValue))}`,
            {
              // TODO(local-auth-bypass): Bearer auth header.
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          ),
          fetch(
            `${apiUrl}/mesh/inlet-z?re=${encodeURIComponent(String(reValue))}`,
            {
              // TODO(local-auth-bypass): Bearer auth header.
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          ),
          fetch(
            `${apiUrl}/mesh/outlet-control?re=${encodeURIComponent(
              String(reValue),
            )}`,
            {
              // TODO(local-auth-bypass): Bearer auth header.
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          ),
        ]);

        // TODO(local-auth-bypass): 401 handling and token cleanup.
        if (
          xResponse.status === 401 ||
          zResponse.status === 401 ||
          outletResponse.status === 401
        ) {
          localStorage.removeItem('access_token');
          document.cookie = 'access_token=; path=/; max-age=0';
          router.replace('/login');
          return;
        }

        if (!xResponse.ok || !zResponse.ok || !outletResponse.ok) {
          throw new Error(
            `HTTP error while loading sections: ${xResponse.status}, ${zResponse.status}, ${outletResponse.status}`,
          );
        }

        const xJson: SectionResponse = await xResponse.json();
        const zJson: SectionResponse = await zResponse.json();
        const outletJson: SectionResponse = await outletResponse.json();

        if (cancelled) return;

        setXInlet('x_inlet' in xJson ? (xJson.x_inlet ?? null) : null);
        setZInlet('z_inlet' in zJson ? (zJson.z_inlet ?? null) : null);
        setOutlet('outlet' in outletJson ? (outletJson.outlet ?? null) : null);
      } catch (err) {
        if (cancelled) return;

        setError(err instanceof Error ? err.message : 'Unknown error');
        setXInlet(null);
        setZInlet(null);
        setOutlet(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadSections();

    return () => {
      cancelled = true;
    };
  }, [reValue, router]);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateRows: 'repeat(3, minmax(0, 1fr))',
        gap: 2,
        height: '100%',
        minHeight: 0,
      }}
    >
      {error && (
        <Alert severity='error' onClose={() => setError(null)}>
          <strong>Section viewer error:</strong> {error}
        </Alert>
      )}

      <SectionCard
        title='Inlet main coronary - velocity magnitude'
        description='Main coronary inlet – velocity field imposed at the inflow boundary of the patient-specific geometry, representing the hemodynamic conditions generated by cardiac activity. Refer to the colorbar for velocity magnitude.'
        dataset={xInlet}
        activeScalar='controlled_velocity_magnitude'
        loading={loading}
        viewDirection='x+'
        scalarRange={sharedScalarRange}
      />

      <SectionCard
        title='Inlet Bypass - velocity magnitude'
        description='Bypass graft inlet – prescribed velocity field at the graft entrance of the patient-specific geometry, representing flow supplied through the surgical graft. Refer to the colorbar for velocity magnitude.'
        dataset={zInlet}
        activeScalar='controlled_velocity_magnitude'
        loading={loading}
        viewDirection='z+'
        scalarRange={sharedScalarRange}
      />

      <SectionCard
        title='Outlet - velocity magnitude'
        description='Outlet section – prescribed velocity field at the outlet of the patient-specific geometry, determined by the optimal control problem. Refer to the colorbar for velocity magnitude.'
        dataset={outlet}
        activeScalar='controlled_velocity_magnitude'
        loading={loading}
        viewDirection='z-'
        scalarRange={sharedScalarRange}
      />
    </Box>
  );
}
